import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import ClearTwoToneIcon from '@mui/icons-material/ClearTwoTone';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';

const SCHEDULE_DATE_VALUE_FORMAT = 'YYYY-MM-DD';
const SCHEDULE_DATE_DISPLAY_FORMAT = 'DD/MM/YYYY';

const createInitialForm = () => ({
  candidateId: '',
  fullName: '',
  mobile: '',
  sapCode: '',
  panNumber: '',
  scheduleDate: '',
  scheduleTime: '',
  agentMappingId: '',
  includePreviousCallSummary: false
});

const buildScheduleIso = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return '';
  const composed = new Date(`${dateValue}T${timeValue}`);
  return Number.isNaN(composed.getTime()) ? '' : composed.toISOString();
};

const getApiErrorMessage = (error, fallback) => (
  error?.response?.data?.status?.value
  || error?.response?.data?.message
  || error?.message
  || fallback
);

export default function BranchCandidateForm({
  open,
  onClose,
  onSave,
  onMessage,
  agents,
  token,
  mode = 'create',
  candidates = []
}) {
  const isScheduleOnly = mode === 'schedule';
  const [form, setForm] = useState(() => createInitialForm());
  const [submitting, setSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);

  const showError = (message) => {
    onMessage?.(message, 'error');
  };

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const chooseResume = (event) => {
    setResumeFile(event.target.files?.[0] || null);
  };

  const uploadResumeForCandidate = async (candidateId) => {
    if (!resumeFile || !candidateId) return;
    const resumeFormData = new FormData();
    resumeFormData.append('resume', resumeFile);
    resumeFormData.append('candidateId', candidateId);
    await axiosInstance.post(nipponBotApiEndpoints.branchManagerResumeUpload, resumeFormData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isScheduleOnly && !form.candidateId) return showError('Select a candidate.');
    if (!isScheduleOnly && !form.fullName.trim()) return showError('Candidate name is required.');
    if (!isScheduleOnly && !form.mobile.trim()) return showError('Candidate mobile number is required.');
    if (!isScheduleOnly && !resumeFile) return showError('Resume upload is required.');
    if (!form.sapCode.trim()) return showError('SAP Code is required.');
    if (!form.agentMappingId) return showError('Select a language.');

    const scheduledAt = buildScheduleIso(form.scheduleDate, form.scheduleTime);
    if (!scheduledAt) return showError('Select a valid interview date and time.');
    if (new Date(scheduledAt).getTime() <= Date.now()) return showError('Please select a future date and time for the interview.');

    setSubmitting(true);
    try {
      const authHeaders = { Authorization: `Bearer ${token}` };
      let candidateId = form.candidateId;

      if (!isScheduleOnly) {
        const createResponse = await axiosInstance.post(nipponBotApiEndpoints.branchManagerCandidateCreate, {
          fullName: form.fullName,
          mobile: form.mobile,
          sapCode: form.sapCode,
          panNumber: form.panNumber
        }, { headers: authHeaders });

        const createdCandidate = createResponse?.data?.data;
        if (!createdCandidate?.id) throw new Error('Candidate was not created successfully.');
        candidateId = createdCandidate.id;

        await uploadResumeForCandidate(createdCandidate.id);
      }

      await axiosInstance.post(nipponBotApiEndpoints.branchManagerInterviewCreate, {
        candidateId,
        agentMappingId: form.agentMappingId,
        scheduledAt,
        sapCode: form.sapCode,
        includePreviousCallSummary: form.includePreviousCallSummary === true
      }, { headers: authHeaders });

      onSave();
      setForm(createInitialForm());
      setResumeFile(null);
      onClose();
    } catch (error) {
      if (error?.response?.status === 401) {
        showError('Your session has expired. Please log in again.');
      } else {
        showError(getApiErrorMessage(error, 'Failed to upload candidate. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: { xs: '100vw', sm: '80vw', md: '50vw', lg: '50vw' },
          maxWidth: '100vw',
          backgroundColor: '#fff',
          boxShadow: 24,
          zIndex: 1300,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fff',
            zIndex: 999
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>{isScheduleOnly ? 'Schedule Interview' : 'Add Candidate'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {isScheduleOnly ? 'Schedule an interview for an existing candidate' : 'Create a new candidate and schedule an interview'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <ClearTwoToneIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 2, minHeight: 0 }}>
          {!isScheduleOnly ? (
            <>
              <TextField
                label="Hiring Manager SAP Code"
                value={form.sapCode}
                onChange={handleChange('sapCode')}
                fullWidth
                required
                sx={{ mb: 3 }}
              />

              <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>Candidate Details</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the candidate's information below.
              </Typography>

              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 3 }}>
                <TextField label="Candidate Name" value={form.fullName} onChange={handleChange('fullName')} fullWidth required />
                <TextField label="Mobile Number" value={form.mobile} onChange={handleChange('mobile')} fullWidth required />
                <TextField label="PAN Number" value={form.panNumber} onChange={handleChange('panNumber')} fullWidth />
                <Button variant="outlined" component="label" sx={{ textTransform: 'none', justifyContent: 'flex-start', minHeight: 56 }} fullWidth>
                  {resumeFile ? `Resume: ${resumeFile.name}` : 'Upload Resume *'}
                  <input hidden type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" onChange={chooseResume} />
                </Button>
              </Box>

              <Box sx={{ borderTop: '1px solid #e0e0e0', my: 3 }} />
            </>
          ) : null}

          <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>Schedule Interview</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isScheduleOnly ? 'Select an existing candidate, then choose the interview date, time, and language.' : 'Select the interview date, time, and language.'}
          </Typography>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 3 }}>
            {isScheduleOnly ? (
              <Autocomplete
                options={candidates}
                value={candidates.find((candidate) => candidate.id === form.candidateId) || null}
                onChange={(event, value) => setForm((current) => ({ ...current, candidateId: value?.id || '' }))}
                getOptionLabel={(candidate) => (
                  `${candidate?.fullName || 'Unnamed Candidate'}${candidate?.sapCode ? ` - ${candidate.sapCode}` : ''}`
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Candidate"
                    placeholder="Search by name or SAP code"
                    fullWidth
                  />
                )}
                sx={{ gridColumn: { sm: '1 / -1' } }}
              />
            ) : null}
            {isScheduleOnly ? (
              <TextField label="SAP Code" value={form.sapCode} onChange={handleChange('sapCode')} fullWidth required />
            ) : null}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Interview Date"
                format={SCHEDULE_DATE_DISPLAY_FORMAT}
                value={form.scheduleDate ? moment(form.scheduleDate, SCHEDULE_DATE_VALUE_FORMAT) : null}
                onChange={(value) => setForm((current) => ({
                  ...current,
                  scheduleDate: value && value.isValid() ? value.format(SCHEDULE_DATE_VALUE_FORMAT) : ''
                }))}
                disablePast
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            <TextField
              type="time"
              label="Interview Time"
              value={form.scheduleTime}
              onChange={handleChange('scheduleTime')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="branch-candidate-agent-label">Language</InputLabel>
              <Select
                labelId="branch-candidate-agent-label"
                label="Language"
                value={form.agentMappingId}
                onChange={handleChange('agentMappingId')}
              >
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>{agent.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? <><CircularProgress size={18} sx={{ color: 'white', mr: 1 }} />{isScheduleOnly ? 'Scheduling...' : 'Uploading...'}</> : (isScheduleOnly ? 'Schedule Interview' : 'Upload')}
            </Button>
          </Box>
        </Box>
      </Box>

      {open && (
        <Box
          onClick={onClose}
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 1200,
            display: { xs: 'none', md: 'block' }
          }}
        />
      )}
    </>
  );
}
